import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import { offerTypeService, OfferType } from "../../apis/offerTypes";
import CreateOfferTypeModal from "./CreateOfferTypeModal";
import EditOfferTypeModal from "./EditOfferTypeModal";

const OfferTypeList: React.FC = () => {
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedOfferType, setSelectedOfferType] = useState<OfferType | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchOfferTypes();
  }, [page, rowsPerPage]);

  const fetchOfferTypes = async () => {
    setLoading(true);
    try {
      const response = await offerTypeService.getOfferTypes(page + 1, rowsPerPage);
      console.log("Offer Types API Response:", response);
      // Handle both boolean true and string "success"
      if (response.status === "success" || response.status === true) {
        console.log("Offer Types Data:", response.data);
        console.log("Offer Types Array:", response.data?.offer_types);
        const offerTypesArray = response.data?.offer_types || [];
        console.log("Setting offer types:", offerTypesArray);
        setOfferTypes(offerTypesArray);
        setTotal(response.data?.pagination?.total || 0);
      } else {
        console.warn("Unexpected response status:", response.status);
        setSnackbar({
          open: true,
          message: "Unexpected response format",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("Error fetching offer types:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch offer types",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOfferType = () => {
    setSelectedOfferType(null);
    setOpenCreateModal(true);
  };

  const handleEditOfferType = (offerType: OfferType) => {
    setSelectedOfferType(offerType);
    setOpenEditModal(true);
  };

  const handleDeleteOfferType = (offerType: OfferType) => {
    setSelectedOfferType(offerType);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedOfferType) return;
    try {
      await offerTypeService.deleteOfferType(selectedOfferType._id);
      setSnackbar({
        open: true,
        message: "Offer type deleted successfully",
        severity: "success",
      });
      setOpenDeleteDialog(false);
      setSelectedOfferType(null);
      fetchOfferTypes();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete offer type",
        severity: "error",
      });
    }
  };

  const handleCloseModals = () => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    setSelectedOfferType(null);
    fetchOfferTypes();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Offer Types Management</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateOfferType} startIcon={<Add />}>
          Add Offer Type
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Partner %</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : offerTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No offer types found
                </TableCell>
              </TableRow>
            ) : (
              offerTypes.map((offerType) => (
                <TableRow key={offerType._id}>
                  <TableCell>{offerType.name}</TableCell>
                  <TableCell>{offerType.partner_percentage}%</TableCell>
                  <TableCell>{offerType.description || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={offerType.is_active ? "Active" : "Inactive"}
                      color={offerType.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditOfferType(offerType)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteOfferType(offerType)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <CreateOfferTypeModal open={openCreateModal} onClose={handleCloseModals} />
      <EditOfferTypeModal
        open={openEditModal}
        onClose={handleCloseModals}
        offerType={selectedOfferType}
      />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Offer Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedOfferType?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default OfferTypeList;

